import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Heart, Sparkles, Smile } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function AffirmationsGuide() {
  const navigate = useNavigate();

  const categories = [
    {
      title: "Self-Worth & Confidence",
      affirmations: [
        "I am enough exactly as I am",
        "I deserve love, respect, and kindness",
        "My worth is not determined by others' opinions",
        "I am confident in my abilities and decisions",
        "I embrace my imperfections as part of my beauty"
      ]
    },
    {
      title: "Health & Body",
      affirmations: [
        "I honor my body with nourishing choices",
        "I am strong, healthy, and full of energy",
        "My body carries me through life—I am grateful for it",
        "I release stress and welcome peace into my body",
        "I am getting healthier every day"
      ]
    },
    {
      title: "Growth & Resilience",
      affirmations: [
        "I am capable of handling whatever comes my way",
        "Challenges help me grow stronger and wiser",
        "I learn from my mistakes without judgment",
        "I am constantly evolving into my best self",
        "My past does not define my future"
      ]
    },
    {
      title: "Abundance & Success",
      affirmations: [
        "I attract opportunities that align with my purpose",
        "I am worthy of success and abundance",
        "I create the life I desire through my actions",
        "Money flows to me easily and effortlessly",
        "I am open to receiving good things"
      ]
    },
    {
      title: "Relationships & Connection",
      affirmations: [
        "I attract loving, supportive relationships",
        "I set healthy boundaries with ease",
        "I communicate my needs clearly and kindly",
        "I am surrounded by people who lift me up",
        "I give and receive love freely"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">Week 1 • Day 3 Guide</span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">Morning Affirmations</h1>
          <p className="text-xl text-secondary-600">Rewire Your Mind for Confidence and Success</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">The Science Behind Affirmations</h2>
          <p className="text-secondary-700 mb-4">
            Affirmations aren't just "positive thinking"—they're a neuroscience-backed tool for rewiring your brain. When you repeat affirmations, you activate the same reward centers that light up when something good actually happens. Over time, this creates new neural pathways that make positive self-perception your default.
          </p>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <p className="text-secondary-800"><strong>Research shows:</strong> Regular affirmation practice reduces stress, improves problem-solving under pressure, and increases self-compassion. The key is consistency and emotional engagement.</p>
          </div>
        </section>

        <section className="bg-gradient-to-r from-amber-400 to-orange-400 rounded-2xl p-6 text-white mb-6">
          <h3 className="font-bold text-lg mb-2">How to Practice</h3>
          <ul className="space-y-2 text-amber-50">
            <li>• Stand in front of a mirror, make eye contact with yourself</li>
            <li>• Speak each affirmation aloud with conviction (not mumbling)</li>
            <li>• Repeat each one 3 times, feeling the words as you say them</li>
            <li>• Practice every morning before starting your day</li>
          </ul>
        </section>

        <section className="space-y-6 mb-6">
          {categories.map((cat, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <h3 className="text-lg font-bold text-amber-700 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />{cat.title}
              </h3>
              <div className="grid gap-2">
                {cat.affirmations.map((aff, i) => (
                  <div key={i} className="bg-amber-50 rounded-lg p-3 text-secondary-700 italic">"{aff}"</div>
                ))}
              </div>
            </div>
          ))}
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-xl font-serif font-bold text-secondary-900 mb-4">Create Your Personal Affirmations</h2>
          <p className="text-secondary-600 mb-4">The most powerful affirmations are ones you create for yourself. Use these guidelines:</p>
          <ul className="space-y-2 text-secondary-700">
            <li>• <strong>Start with "I am" or "I"</strong> — present tense, as if already true</li>
            <li>• <strong>Keep them positive</strong> — say what you ARE, not what you're NOT</li>
            <li>• <strong>Make them specific</strong> — "I am confident in meetings" vs. just "I am confident"</li>
            <li>• <strong>Include emotion</strong> — how do you want to FEEL?</li>
            <li>• <strong>Believe they're possible</strong> — stretch yourself but stay believable</li>
          </ul>
        </section>
      </main>
    </div>
  );
}

export default AffirmationsGuide;
